import type { MovementConfig } from '../config/movements';
import type { KitGroup, TransferDetails, TransportDetails, WizardLine } from '../types/wizard';
import { kitGroupsFromWizardLines } from '../utils/kitDocument';
import {
  resolveStandardTransferType,
  resolveTransferOutDocSubType,
  shouldBuildKitItems,
} from '../utils/postingVariant';
import { componentRequiredQty } from '../utils/lineQuantities';
import type { RfidDocumentRow } from './rfid.service';
import { formatDateEnGb, getDocumentItems } from '../utils/document';
import { effectiveWhQty, type KitBomItem } from '../utils/tagAllocation';
import { componentQtyFields } from '../utils/lineQuantities';

function transportBlock(t: TransportDetails) {
  const distance = String(t.distance ?? '').trim();
  return {
    TRANSPORTER_CODE: t.TRANSPORTER_CODE,
    VEHICLE_NO: t.VEHICLE_NO,
    VEHICLE_TYPE: t.VEHICLE_TYPE,
    DRIVER_NAME: t.DRIVER_NAME,
    DRIVER_MOB_NO: t.DRIVER_MOB_NO,
    TRANPORTATION_BY: t.TRANPORTATION_BY,
    INSPECTEDBY: t.INSPECTEDBY,
    LR_NO: t.LR_NO,
    DIRECT_DISPLATCH: t.DIRECT_DISPLATCH ?? 'N',
    ...(distance ? { DISTANCE: distance } : {}),
    REASON: t.reason,
    CONTAINER_SEAL: t.containerSeal,
    EWAY_TRANSTYPE: t.eway,
    POD_RECIEVED: t.pod,
    TRANSPORT_MODE: t.TRANSPORT_MODE,
    TRANSPORT_NAME: String(t.TRANSPORTER_NAME ?? '').trim(),
    INVOICE_1: '',
  };
}

function standardItemPayload(
  line: WizardLine,
  movement: MovementConfig,
  quantityField: number,
  toWh = ''
) {
  const { dispRecQty, scanQty, nonScanQty } = componentQtyFields(line, quantityField);
  return {
    ITEM_NO: line.itemNo,
    DISP_REC_QTY: String(dispRecQty),
    SCAN_QTY: String(scanQty),
    NON_SCAN_QTY: String(nonScanQty),
    FROM_WH: '',
    FROM_BAY: line.fromBay,
    FROM_BIN: line.fromBin || movement.fromBin || '',
    TO_WH: toWh,
    TO_BAY: line.toBay || movement.toBay || '',
    TO_BIN: line.toBin || movement.toBin || '',
    KIT_CODE: '',
    KIT_QTY: '',
    Short_Qty:
      movement.id === 'dehire_received' ? String(line.shortQty ?? 0) : '',
    DAMAGED_QTY: '',
    MATERIAL_CODE: line.materialCode,
    MATERIAL_DESC: line.materialDesc,
    MATERIAL_GROUP_CODE: line.materialGroupCode ?? '',
    MATERIAL_GROUP_NAME: line.materialGroupName ?? '',
    RFID_CODES: {
      items: line.rfidCodes.map((RFID_CODE) => ({ RFID_CODE })),
    },
  };
}

/** Kit document submit — mirrors legacy LeapOutTabs / kit movements. */
function buildKitStandardItems(
  kitGroups: KitGroup[],
  kitBom: KitBomItem[] | null,
  movement: MovementConfig,
  toWh = ''
): { items: Record<string, unknown>[]; sender: string; senderName: string } {
  const items: Record<string, unknown>[] = [];
  let sender = '';
  let senderName = '';

  kitGroups.forEach((g, kitIndex) => {
    const header = g.lines.find((l) => l.isKitHeader);
    const components = g.lines.filter((l) => !l.isKitHeader);

    if (header) {
      items.push({
        ITEM_NO: header.itemNo,
        DISP_REC_QTY: String(g.kitQty),
        SCAN_QTY: '0',
        NON_SCAN_QTY: String(g.kitQty),
        FROM_WH: '',
        FROM_BAY: '',
        FROM_BIN: movement.fromBin ?? 'CONDITION',
        TO_WH: toWh,
        TO_BAY: '',
        TO_BIN: '',
        KIT_CODE: '',
        KIT_QTY: '',
        Short_Qty: '',
        DAMAGED_QTY: '',
        MATERIAL_CODE: header.materialCode,
        MATERIAL_DESC: header.materialDesc,
        MATERIAL_GROUP_CODE: header.materialGroupCode ?? '',
        MATERIAL_GROUP_NAME: header.materialGroupName ?? '',
        RFID_CODES: { items: [{ RFID_CODE: '' }] },
      });
      sender = g.sender || header.sender;
      senderName = g.senderName || header.senderName;
    }

    components.forEach((line) => {
      const whQty = effectiveWhQty(line, kitGroups, kitIndex, kitBom);
      const { dispRecQty } = componentQtyFields(line, whQty);
      if (dispRecQty > whQty) {
        throw new Error(
          `Quantity exceeded for ${line.materialCode}. Max: ${whQty}, Entered: ${dispRecQty}`
        );
      }
      items.push(standardItemPayload(line, movement, whQty, toWh));
    });
  });

  items.sort(
    (a, b) =>
      parseInt(String(a.ITEM_NO ?? '0'), 10) - parseInt(String(b.ITEM_NO ?? '0'), 10)
  );

  return { items, sender, senderName };
}

export function buildStandardPayload(params: {
  movement: MovementConfig;
  userId: string;
  document: RfidDocumentRow;
  effectiveDate: Date;
  transport: TransportDetails;
  lines: WizardLine[];
  kitGroups: KitGroup[];
  kitBom: KitBomItem[] | null;
  remarks: string;
  variant: 'kit' | 'flat';
  useAutoTransferType: boolean;
}): Record<string, unknown> {
  const {
    movement,
    userId,
    document,
    effectiveDate,
    transport,
    lines,
    kitGroups,
    kitBom,
    remarks,
    variant,
    useAutoTransferType,
  } = params;

  const transferType = resolveStandardTransferType(movement, useAutoTransferType);
  const isDehireIn = movement.id === 'dehire_received';
  // CUSTOUT line items carry warehouse TO_WH (e.g. 9000). LEAPINAUTO still returns
  // "Enter Plant" when this is blank / wrongly set to plant (1014); pass the doc value.
  const docToWh = isDehireIn
    ? String(getDocumentItems(document)[0]?.TO_WH ?? '').trim()
    : '';

  let items: Record<string, unknown>[];
  let sender = String(document.Sender ?? '');
  let senderName = String(document.SenderName ?? '');

  if (shouldBuildKitItems(kitGroups, variant, lines)) {
    const groupsForSubmit =
      kitGroups.length > 0 ? kitGroups : kitGroupsFromWizardLines(lines);
    const kitPayload = buildKitStandardItems(groupsForSubmit, kitBom, movement, docToWh);
    items = kitPayload.items;
    sender = kitPayload.sender || sender;
    senderName = kitPayload.senderName || senderName;
  } else {
    const postLines = lines.filter((l) => !l.isKitHeader);
    const senderLine = postLines.find((l) => l.sender.trim()) ?? postLines[0];
    // Prefer line sender when set; empty string must not wipe document.Sender
    // (Dehire Received / LEAPIN line items have no Sender — header does).
    sender = senderLine?.sender?.trim() || sender;
    senderName = senderLine?.senderName?.trim() || senderName;
    items = postLines.map((line) =>
      standardItemPayload(line, movement, line.quantity, docToWh)
    );
  }

  if (isDehireIn) {
    // Header CustName is the customer (SENDER), not the LEAP plant (RECEIVER).
    senderName = senderName || String(document.CustName ?? '');
  }

  return {
    Request_Data: {
      UserId: userId,
      INDICATOR: 'C',
      TRANSFER_TYPE: transferType,
      SENDER: sender,
      SENDER_NAME: senderName,
      // Keep BP-style RECEIVER (PL1014). Stripping to 1014 cleared "Enter Plant"
      // but SAP returned empty Response_Data — partner must stay PL….
      RECEIVER: String(document.Receiver ?? ''),
      RECEIVER_NAME: isDehireIn
        ? String(document.ReceiverName ?? '')
        : String(document.CustName ?? document.ReceiverName ?? ''),
      REF_DOC: String(document.DocumentNo ?? ''),
      EFFECTIVE_DATE: formatDateEnGb(effectiveDate),
      MODEL_TYPE: movement.modelType ?? '102',
      REMARKS: `${remarks}(RFID App)`,
      TRANSPORT_DETAILS: transportBlock(transport),
      ITEMS: items,
    },
  };
}

export function buildRelocationPayload(params: {
  movement: MovementConfig;
  userId: string;
  document: RfidDocumentRow;
  effectiveDate: Date;
  transport: TransportDetails;
  lines: WizardLine[];
  remarks: string;
}): Record<string, unknown> {
  const { movement, userId, document, effectiveDate, transport, lines, remarks } = params;
  const isRelOut =
    movement.id === 'relocation_out' ||
    String(movement.transferTypeFlat ?? '').toUpperCase().includes('OUT');

  const items = lines
    .filter((l) => !l.isKitHeader)
    .map((line) => {
      const { dispRecQty } = componentQtyFields(line, line.quantity);
      const fromBay = String(line.fromBay ?? '').trim();
      const fromBin =
        String(line.fromBin ?? '').trim() ||
        String(movement.fromBin ?? '').trim() ||
        'REPAIR';
      const qty = String(dispRecQty);
      const item: Record<string, unknown> = {
        ItemNo: line.itemNo,
        ...(isRelOut ? { RelOutQty: qty } : { RelInQty: qty }),
        FromBAY: fromBay,
        FromBIN: fromBin,
        RFIDCodes: {
          item: line.rfidCodes.map((RFIDCode) => ({ RFIDCode })),
        },
      };
      return item;
    });

  const sender = String(lines[0]?.sender ?? document.Plant ?? document.Sender ?? '').trim();
  const senderName = String(lines[0]?.senderName ?? document.PlantName ?? '').trim();
  const receiver = String(document.Customer ?? document.Receiver ?? '').trim();
  const receiverName = String(document.CustName ?? '').trim();

  return {
    Request_Data: {
      UserID: userId,
      Indicator: 'C',
      TransferType: movement.transferTypeFlat ?? 'REL_OUT',
      ...(sender ? { Sender: sender } : {}),
      ...(senderName ? { SenderName: senderName } : {}),
      ...(receiver ? { Receiver: receiver } : {}),
      ...(receiverName ? { ReceiverName: receiverName } : {}),
      RefDoc: String(document.DocumentNo ?? ''),
      EffectiveDate: formatDateEnGb(effectiveDate),
      ModelType: movement.modelType ?? '111',
      Remarks: `${remarks}(RFID App)`,
      TransportDetails: {
        TransporterCode: transport.TRANSPORTER_CODE || '0',
        VehicleNo: transport.VEHICLE_NO || 'N',
        VehicleType: transport.VEHICLE_TYPE || 'N',
        DriverName: transport.DRIVER_NAME || 'N',
        DriverMobNo: transport.DRIVER_MOB_NO || '0',
        TransportationBy: transport.TRANPORTATION_BY || 'N',
        InspectedBy: transport.INSPECTEDBY || 'N',
        LrNo: transport.LR_NO || 'N',
        DirectDisplatch: transport.DIRECT_DISPLATCH || 'N',
        ...(String(transport.distance ?? '').trim()
          ? { Distance: String(transport.distance).trim() }
          : {}),
        Reason: transport.reason,
        ContainerSeal: transport.containerSeal || 'N',
        EwayTransType: transport.eway || 'N',
        PodReceived: transport.pod || 'N',
        TransportMode: transport.TRANSPORT_MODE || '0',
        TransporterName: String(transport.TRANSPORTER_NAME ?? '').trim(),
        Invoice1: 'N',
      },
      items: { item: items },
    },
  };
}

function formatDateIso(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function buildTransferOutItems(
  lines: WizardLine[],
  kitGroups: KitGroup[],
  kitBom: KitBomItem[] | null,
  movement: MovementConfig
): Record<string, unknown>[] {
  const items: Record<string, unknown>[] = [];
  let seq = 10;

  const pushComponent = (
    line: WizardLine,
    kitIndex: number,
    kitItemNo: string
  ) => {
    const required = componentRequiredQty(line, kitGroups, kitIndex, kitBom);
    const { dispRecQty, scanQty, nonScanQty } = componentQtyFields(line, required);
    if (dispRecQty <= 0) return;
    items.push({
      ItemNo: String(seq),
      ItemCode: line.materialCode,
      Quantity: String(dispRecQty),
      ScanQty: String(scanQty),
      NonScanQty: String(nonScanQty),
      Plant: line.sender || '9000',
      KITItem: kitItemNo,
      RFID_Codes: {
        item: line.rfidCodes.length
          ? line.rfidCodes.map((RFID_Code) => ({ RFID_Code }))
          : [{ RFID_Code: '' }],
      },
    });
    seq += 10;
  };

  if (kitGroups.length > 0) {
    kitGroups.forEach((g, kitIndex) => {
      const kitItemNo = String(seq);
      seq += 10;
      g.lines
        .filter((l) => !l.isKitHeader && l.materialCode.trim())
        .forEach((line) => pushComponent(line, kitIndex, kitItemNo));
    });
  } else {
    lines
      .filter((l) => !l.isKitHeader && l.materialCode.trim())
      .forEach((line) => pushComponent(line, 0, ''));
  }

  return items;
}

export function buildTransferOutPayload(params: {
  movement: MovementConfig;
  bpCode: string;
  bpName: string;
  toCustomerCode: string;
  effectiveDate: Date;
  lines: WizardLine[];
  kitGroups: KitGroup[];
  kitBom: KitBomItem[] | null;
  remarks: string;
  transport: TransportDetails;
  transferDetails: TransferDetails;
  lineOfBusiness?: string;
  variant: 'kit' | 'flat';
  useAutoTransferType: boolean;
}): Record<string, unknown> {
  const {
    movement,
    bpCode,
    bpName,
    toCustomerCode,
    effectiveDate,
    lines,
    kitGroups,
    kitBom,
    remarks,
    transport,
    transferDetails,
    lineOfBusiness,
    variant,
    useAutoTransferType,
  } = params;

  const docSubType = resolveTransferOutDocSubType(movement, useAutoTransferType);

  return {
    Request_Data: {
      BP_Code: bpCode,
      BP_Name: bpName,
      Posting_date: formatDateIso(new Date()),
      Document_Date: formatDateIso(effectiveDate),
      Transfer_Type: '101',
      To_Customer: toCustomerCode,
      Vehicle_Number: transport.VEHICLE_NO,
      Transporter_Name: transferDetails.transporterName,
      Invoice_No: transferDetails.invoiceNo,
      Document_Number: transferDetails.lrNo,
      Remark: `${remarks}(RFID App)`,
      LoB: lineOfBusiness ?? '',
      DOC_SUB_TYPE: docSubType ?? 'TROUT',
      Items: buildTransferOutItems(lines, kitGroups, kitBom, movement),
    },
  };
}

export function buildTransferInPayload(params: {
  movement: MovementConfig;
  userId: string;
  document: RfidDocumentRow;
  effectiveDate: Date;
  transport: TransportDetails;
  lines: WizardLine[];
  kitGroups: KitGroup[];
  kitBom: KitBomItem[] | null;
  remarks: string;
  variant: 'kit' | 'flat';
  useAutoTransferType: boolean;
}): Record<string, unknown> {
  const {
    movement,
    userId,
    document,
    effectiveDate,
    transport,
    lines,
    kitGroups,
    kitBom,
    remarks,
    variant,
    useAutoTransferType,
  } = params;

  const transferType = resolveStandardTransferType(movement, useAutoTransferType);

  const items: Record<string, unknown>[] = [];

  const entries =
    kitGroups.length > 0
      ? kitGroups.flatMap((g, kitIndex) =>
          g.lines
            .filter((l) => !l.isKitHeader && l.materialCode.trim())
            .map((line) => ({ line, kitIndex }))
        )
      : lines
          .filter((l) => !l.isKitHeader && l.materialCode.trim())
          .map((line) => ({ line, kitIndex: 0 }));

  for (const { line, kitIndex } of entries) {
    const required = componentRequiredQty(line, kitGroups, kitIndex, kitBom);
    const { dispRecQty, scanQty, nonScanQty } = componentQtyFields(line, required);
    items.push({
      ITEM_NO: line.itemNo,
      DISP_REC_QTY: String(dispRecQty),
      SCAN_QTY: String(scanQty),
      NON_SCAN_QTY: String(nonScanQty),
      FROM_WH: line.sender || String(document.Sender ?? ''),
      FROM_BIN: line.fromBin || movement.fromBin || 'DEHIRE',
      TO_BAY: line.toBay || movement.toBay || 'B001',
      TO_BIN: line.toBin || movement.toBin || 'SORTING',
      Short_Qty: '0',
      RFID_CODES: {
        items: line.rfidCodes.map((RFID_CODE) => ({ RFID_CODE })),
      },
    });
  }

  return {
    Request_Data: {
      UserId: userId,
      INDICATOR: 'C',
      TRANSFER_TYPE: transferType ?? 'TRIN',
      SENDER: String(document.Sender ?? ''),
      RECEIVER: String(document.Receiver ?? document.Customer ?? ''),
      REF_DOC: String(document.DocumentNo ?? ''),
      EFFECTIVE_DATE: formatDateEnGb(effectiveDate),
      MODEL_TYPE: movement.modelType ?? '113',
      REMARKS: `${remarks}(RFID App)`,
      TRANSPORT_DETAILS: {
        TRANSPORTER_CODE: transport.TRANSPORTER_CODE || '',
        VEHICLE_NO: transport.VEHICLE_NO,
        VEHICLE_TYPE: transport.VEHICLE_TYPE || 'DefaultVehicleType',
        DRIVER_NAME: transport.DRIVER_NAME || 'Default Driver',
        DRIVER_MOB_NO: transport.DRIVER_MOB_NO || '',
        TRANPORTATION_BY: transport.TRANPORTATION_BY || 'DefaultTransportation',
        INSPECTEDBY: transport.INSPECTEDBY || 'DefaultInspector',
        LR_NO: transport.LR_NO,
        TRANSPORT_NAME: String(transport.TRANSPORTER_NAME ?? '').trim() || transport.TRANSPORTER_CODE || 'DefaultTransporter',
        DIRECT_DISPLATCH: transport.DIRECT_DISPLATCH || 'N',
        POD_RECIEVED: transport.pod || 'N',
        TRANSPORT_MODE: transport.TRANSPORT_MODE,
        EWAY_TRANSTYPE: transport.eway || 'NA',
        ...(String(transport.distance ?? '').trim()
          ? { DISTANCE: String(transport.distance).trim() }
          : {}),
        REASON: transport.reason || 'check',
        CONTAINER_SEAL: transport.containerSeal || '',
        INVOICE_1: '',
      },
      ITEMS: items,
    },
  };
}

export function buildSubmitPayload(
  movement: MovementConfig,
  ctx: {
    userId: string;
    document: RfidDocumentRow;
    effectiveDate: Date;
    transport: TransportDetails;
    lines: WizardLine[];
    kitGroups: KitGroup[];
    kitBom: KitBomItem[] | null;
    remarks: string;
    variant: 'kit' | 'flat';
    transferBpCode?: string;
    transferToBpCode?: string;
    transferToCustomerName?: string;
    transferDetails?: TransferDetails;
    transferLineOfBusiness?: string;
    transferCustomerName?: string;
    useAutoTransferType: boolean;
  }
): Record<string, unknown> {
  let payload: Record<string, unknown>;
  if (movement.wizardKind === 'relocation') {
    payload = buildRelocationPayload({ movement, ...ctx });
  } else if (movement.wizardKind === 'transferOut') {
    payload = buildTransferOutPayload({
      movement,
      bpCode: ctx.transferBpCode ?? String(ctx.document.Receiver ?? ''),
      bpName: ctx.transferCustomerName ?? String(ctx.document.CustName ?? ''),
      toCustomerCode: ctx.transferToBpCode ?? '',
      effectiveDate: ctx.effectiveDate,
      lines: ctx.lines,
      kitGroups: ctx.kitGroups,
      kitBom: ctx.kitBom,
      remarks: ctx.remarks,
      transport: ctx.transport,
      transferDetails: ctx.transferDetails ?? {
        vehicleNo: '',
        transporterName: '',
        invoiceNo: '',
        lrNo: '',
        location: '',
      },
      lineOfBusiness: ctx.transferLineOfBusiness,
      variant: ctx.variant,
      useAutoTransferType: ctx.useAutoTransferType,
    });
  } else if (movement.wizardKind === 'transferIn') {
    payload = buildTransferInPayload({ movement, ...ctx });
  } else {
    payload = buildStandardPayload({ movement, ...ctx });
  }
  return payload;
}

export type SapSubmitParse = { success: boolean; message: string };

function asSapRecord(value: unknown): Record<string, unknown> | null {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readSapStatus(node: Record<string, unknown>): string {
  return String(node.Status ?? node.STATUS ?? node.status ?? '')
    .trim()
    .toUpperCase();
}

function readSapMessage(node: Record<string, unknown>): string {
  const raw = node.Message ?? node.MESSAGE ?? node.message;
  return raw != null ? String(raw).trim() : '';
}

function sapResponseItems(responseNode: unknown): Record<string, unknown>[] {
  const root = asSapRecord(responseNode);
  if (!root) return [];
  const item = root.item;
  if (Array.isArray(item)) {
    return item
      .map(asSapRecord)
      .filter((x): x is Record<string, unknown> => Boolean(x));
  }
  const single = asSapRecord(item);
  return single ? [single] : [];
}

function collectSapResultNodes(data: unknown): Record<string, unknown>[] {
  const root = asSapRecord(data);
  if (!root) return [];
  const out: Record<string, unknown>[] = [];
  const push = (node: unknown) => {
    const rec = asSapRecord(node);
    if (rec) out.push(rec);
  };

  push(root.Result);
  if (Array.isArray(root.Result)) {
    for (const row of root.Result) push(row);
  }

  if (root.Response) {
    out.push(...sapResponseItems(root.Response));
    push(root.Response);
  }

  const rd = root.Response_Data;
  if (rd && typeof rd === 'object') {
    push(rd);
    const rdo = rd as Record<string, unknown>;
    push(rdo.Result);
    if (Array.isArray(rdo.Result)) {
      for (const row of rdo.Result) push(row);
    }
    if (rdo.Response) {
      out.push(...sapResponseItems(rdo.Response));
      push(rdo.Response);
    }
  }

  return out;
}

/** Parse SAP/CPI document-post bodies (success Status S or business error Status E). */
export function parseSubmitResult(data: unknown): SapSubmitParse {
  if (data == null || typeof data !== 'object') {
    return { success: false, message: 'SAP returned an empty response for this post' };
  }

  const nodes = collectSapResultNodes(data);

  for (const node of nodes) {
    const status = readSapStatus(node);
    if (status === 'S' || status === 'SUCCESS') {
      return { success: true, message: readSapMessage(node) || 'Submitted' };
    }
  }

  for (const node of nodes) {
    const status = readSapStatus(node);
    if (status === 'E' || status === 'ERROR' || status === 'F') {
      return {
        success: false,
        message: readSapMessage(node) || 'Submit failed',
      };
    }
  }

  for (const node of nodes) {
    const message = readSapMessage(node);
    if (message) return { success: false, message };
  }

  const root = data as Record<string, unknown>;
  if (root.Response_Data === '' || (root.Response_Data == null && root.Response == null)) {
    return { success: false, message: 'SAP returned an empty response for this post' };
  }

  return { success: false, message: 'Submit failed' };
}

export function parseSubmitSuccess(data: unknown): string | null {
  const parsed = parseSubmitResult(data);
  return parsed.success ? parsed.message : null;
}

